// P5 (2026-11-02) — Fan-out rollup card.
//
// Shows X of N published, per-plan state (published / failed / in
// progress), and provides retry affordances for failed siblings.
// Data is a live view from GET /publish/fan-out/{id}; the single-plan
// endpoints keep handling preflight/publish/cancel for each sibling.

import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { channelDisplay, useOrgTimezone } from "../lib/tz";

const BUCKET_COLORS = {
  published:   "var(--bo-success, #146c43)",
  failed:      "var(--bo-danger, #a0241b)",
  in_progress: "var(--bo-muted, #777)",
};

export default function PublishFanOutRollup() {
  const { fanOutId } = useParams();
  useOrgTimezone();
  const [rollup, setRollup] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/publish/fan-out/${fanOutId}`);
      setRollup(r.data || null);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [fanOutId]);

  useEffect(() => { load(); }, [load]);

  async function onRetry(planId) {
    setBusy(true);
    try {
      await api.post(`/publish-plans/${planId}/preflight`);
      await api.post(`/publish-plans/${planId}/publish`);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Fan-out rollup">
      <div className="bo-page" data-testid="fan-out-rollup-page"
           style={{ padding: 24 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center",
                      marginBottom: 16, flexWrap: "wrap" }}>
          <Link to="/brand-os/publish/fan-out"
                className="bo-btn bo-btn-ghost"
                style={{ textDecoration: "none" }}>
            ← New fan-out
          </Link>
          <Link to="/brand-os/publish/queue"
                className="bo-btn bo-btn-ghost"
                style={{ textDecoration: "none" }}>
            Queue
          </Link>
          <button className="bo-btn bo-btn-ghost"
                  data-testid="fan-out-rollup-refresh"
                  onClick={load}>
            Refresh
          </button>
        </div>

        <div className="bo-eyebrow">Fan-out</div>
        <h1 className="bo-serif" style={{ fontSize: 24, margin: "4px 0 10px" }}>
          Rollup
        </h1>
        <div style={{ fontSize: 12, color: "var(--bo-muted)",
                      fontFamily: "var(--bo-mono, monospace)" }}>
          {fanOutId}
        </div>

        {err && (
          <div className="bo-card" data-testid="fan-out-rollup-error"
               style={{ padding: 12, color: "var(--bo-danger)",
                        marginTop: 12 }}>
            {typeof err === "string" ? err : err.message || JSON.stringify(err)}
          </div>
        )}

        {rollup && (
          <>
            <div className="bo-card" style={{ padding: 16, marginTop: 16, display: "grid", gap: 10 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}
                   data-testid="fan-out-rollup-summary">
                {rollup.published} of {rollup.total} published
                {rollup.failed > 0 && ` · ${rollup.failed} failed`}
                {rollup.in_progress > 0 && ` · ${rollup.in_progress} in progress`}
              </div>
            </div>

            <div className="bo-card" style={{ padding: 0, marginTop: 16, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead style={{ background: "var(--bo-panel-alt, #faf8f2)" }}>
                  <tr>
                    <th style={{ padding: 10, textAlign: "left" }}>Channel</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Surface</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Status</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Artifact</th>
                    <th style={{ padding: 10, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.plans.map((p) => (
                    <tr key={p.plan_id}
                        data-testid={`fan-out-rollup-row-${p.plan_id}`}
                        style={{ borderTop: "1px solid var(--bo-line)" }}>
                      <td style={{ padding: 10 }}>
                        {channelDisplay(p.channel)}
                      </td>
                      <td style={{ padding: 10 }}>{p.surface}</td>
                      <td style={{ padding: 10,
                                   color: BUCKET_COLORS[p.bucket] || "inherit",
                                   fontWeight: 600 }}>
                        {p.bucket}
                        {p.is_acceptance_test && (
                          <span style={{ marginLeft: 8, fontSize: 10,
                                         padding: "2px 6px", borderRadius: 10,
                                         background: "var(--bo-accent-soft)",
                                         color: "var(--bo-ink)" }}>
                            TEST
                          </span>
                        )}
                      </td>
                      <td style={{ padding: 10 }}>
                        {p.external_artifact_url ? (
                          <a href={p.external_artifact_url}
                             target="_blank"
                             rel="noopener noreferrer"
                             data-testid={`fan-out-rollup-artifact-${p.plan_id}`}>
                            Open ↗
                          </a>
                        ) : "—"}
                      </td>
                      <td style={{ padding: 10, textAlign: "right" }}>
                        {p.receipt_id && (
                          <Link to={`/brand-os/publish/receipts/${p.receipt_id}`}
                                className="bo-link-sm"
                                data-testid={`fan-out-rollup-receipt-${p.plan_id}`}>
                            Receipt
                          </Link>
                        )}
                        {p.bucket === "failed" && (
                          <button
                            className="bo-btn bo-btn-ghost"
                            disabled={busy}
                            data-testid={`fan-out-rollup-retry-${p.plan_id}`}
                            onClick={() => onRetry(p.plan_id)}
                            style={{ marginLeft: 8, fontSize: 12,
                                     padding: "4px 10px" }}
                          >
                            Retry
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}
